import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline';
import { NAV_ICONS } from '@/lib/ui/navIcons';
import { usePermissions } from '../../lib/hooks';
// Direct path, not the barrel: Sidebar.test / Sidebar.rail.test mock `@/lib/hooks` without it.
import { useAppNavRail } from '../../lib/hooks/useAppNavRail';
import { Button } from '@/components/ui/button';
import { TooltipProvider, WithTooltip } from '@/components/ui/tooltip';
import { APP_NAV_RAIL_TOOLTIP_OFFSET } from '@/lib/ui/appNavRail';
import { waitingLabel } from '@/lib/ui/waitingLabel';
import { unreadLabel } from '@/lib/ui/unreadLabel';
import { useAuth } from '../../lib/contexts/AuthContext';
import SidebarSubmenu, { SidebarNavLink } from './SidebarSubmenu';
// The row arrays live in their own module — see its header (a component file
// may not export a non-component, and the visibility suites read the lists).
// `adminNavigation` is NOT rendered here since Story 24.5: every role reaches
// the Settings hub (`/settings`, Story 24.8) through the one role-open
// `settingsNavRow`, and the hub draws `adminNavigation`'s rows as cards.
import { isNavHrefActive, settingsNavRow, superAdminNavigation, visibleNavItems } from './adminNavigation';

type IconComponent = React.ComponentType<{ className?: string }>;

/**
 * A top-level sidebar row (Story 24.5). Outline icon at rest, solid while
 * active (DESIGN.md › Navigation icons). Every row highlights on its own
 * route and on any route under it — there is no exact-match exception any
 * more (Boards used to have one).
 */
interface NavRow {
  name: string;
  href: string;
  icon: IconComponent;
  activeIcon: IconComponent;
}

/**
 * The "Work" group, in rendered order (pinned by `Sidebar.test.tsx`).
 * `/dashboards` keeps its route and is labelled "Boards".
 */
const WORK_NAV: ReadonlyArray<NavRow> = [
  { name: 'Chats', href: '/chat', icon: NAV_ICONS.chats.outline, activeIcon: NAV_ICONS.chats.solid },
  { name: 'Tasks', href: '/tasks', icon: NAV_ICONS.tasks.outline, activeIcon: NAV_ICONS.tasks.solid },
  { name: 'Boards', href: '/dashboards', icon: NAV_ICONS.boards.outline, activeIcon: NAV_ICONS.boards.solid },
  { name: 'Calendar', href: '/calendar', icon: NAV_ICONS.calendar.outline, activeIcon: NAV_ICONS.calendar.solid },
  { name: 'Library', href: '/library', icon: NAV_ICONS.library.outline, activeIcon: NAV_ICONS.library.solid },
];

/** Role-open in-app guide — first row of the "Help & admin" group. */
const DOCUMENTATION_ROW: NavRow = {
  name: 'Documentation',
  href: '/docs',
  icon: NAV_ICONS.documentation.outline,
  activeIcon: NAV_ICONS.documentation.solid,
};

/**
 * Story 24.8 — role-open. `/settings` is the Settings hub, which every role
 * may open (ruled M-2); the hub filters its own cards by role. The row comes
 * from `adminNavigation.ts` so the Sidebar and More draw the same one, and it
 * highlights by prefix on `/settings` and every `/settings/*` page.
 */
const SETTINGS_ROW: NavRow = {
  name: settingsNavRow.name,
  href: settingsNavRow.href,
  icon: settingsNavRow.icon,
  activeIcon: settingsNavRow.activeIcon ?? settingsNavRow.icon,
};

/** The superuser-only Engineering group's title — the submenu's testids key on it. */
const ENGINEERING_TITLE = 'Engineering';

/** Story 24.6 — the pin toggle's `aria-controls` target. */
const SIDEBAR_ID = 'app-sidebar';

/**
 * Story 24.7 — the Sidebar exists only at ≥1024px. Below that there is no
 * drawer, no scrim and no hamburger: the BottomTabBar (`BottomTabBar.tsx`) and
 * the `/more` page carry phone navigation.
 *
 * Story 24.18 — its props are `waitingCount` and (Story 29.2) `unreadCount`,
 * read by `MainLayout` (the ONE chrome reader of `useBriefing`) and drawn as
 * the Tasks row's and the Chats row's badges. This component never calls the
 * hook itself.
 */
export default function Sidebar({
  waitingCount,
  unreadCount,
}: {
  waitingCount?: number;
  unreadCount?: number;
}) {
  const location = useLocation();
  const { isOwner } = usePermissions();
  const { user } = useAuth(); // Story PPA-2.1: superuser status for nav filtering
  // Story 24.6 — `railed` is the 64px icon rail (1024–1279px, unpinned).
  const { band, pinned, railed, togglePinned } = useAppNavRail();

  const [engineeringOpen, setEngineeringOpen] = useState(false);

  // Auto-expand Engineering when one of ITS routes is active.
  //
  // 🔴 IT ONLY EVER SETS `true`, AND THAT IS THE DECISION (review P11).
  // Navigating away does not close it: the person may have opened it on
  // purpose, and an effect keyed on `location.pathname` that collapsed it
  // would slam it shut on their next navigation with no way to keep it open.
  useEffect(() => {
    const active = superAdminNavigation.some((item) =>
      isNavHrefActive(location.pathname, item.href)
    );
    if (active) setEngineeringOpen(true);
  }, [location.pathname]);

  /* `=== true`, not `?? false` (T-20260916-1 L4). `??` guards only
     null/undefined, so a payload carrying the STRING 'true' — or 1, or an
     object — would be truthy and reveal the whole Engineering group (Prompt
     Management, Billing & Credits, Tenants, Byte Cost) — all platform-wide
     surfaces. The widened `isOwner()` already coerces strictly; this agrees
     with it and with the harness helper `permissions/is-superuser.ts`, whose
     contract is that anything other than a literal `true` is false. */
  const viewer = { isOwner: isOwner(), isSuperuser: user?.is_superuser === true };
  // `SidebarSubmenu` returns null when the viewer sees none of its rows; the
  // same predicate is asked here so no empty `<li>` is left behind.
  const showEngineering = visibleNavItems(superAdminNavigation, viewer).length > 0;

  const renderRow = (row: NavRow) => (
    <li key={row.href}>
      <SidebarNavLink
        name={row.name}
        href={row.href}
        icon={row.icon}
        activeIcon={row.activeIcon}
        isActive={isNavHrefActive(location.pathname, row.href)}
        rail={railed}
        badgeCount={
          row.href === '/tasks' ? waitingCount : row.href === '/chat' ? unreadCount : undefined
        }
        badgeLabel={row.href === '/chat' ? unreadLabel : waitingLabel}
      />
    </li>
  );

  const pinLabel = pinned ? 'Collapse sidebar' : 'Expand sidebar';
  const PinIcon = pinned ? ChevronDoubleLeftIcon : ChevronDoubleRightIcon;

  return (
    <>
      {/* Sidebar. `z-40`, the `navPanel` rung (DW-6). It used to sit one notch
          above the phone drawer's dismiss scrim; Story 24.7 deleted the drawer
          and the scrim, and the panel keeps its rung. Ladder:
          `src/lib/ui/zIndexLadder.ts`.

          Story 24.7 — `hidden lg:flex`: below 1024px the panel is not rendered
          at all (the BottomTabBar takes over). Everything from `lg` up is
          unchanged.

          Story 24.5 — light (A3): `bg-canvas` with a `border-line` right
          hairline, and ONE scroll area — the `<nav>` below. The positioning
          and width classes are unchanged from T-20260916-4/-5:

          1. `flex flex-col`, so the inner nav owns the scroll. There is
             deliberately NO `overflow-y-auto` on this element: the aside is
             viewport-bounded and never scrolls; the nav inside it does.

          2. `lg:h-full`. Story 24.7b — MainLayout's root is a bounded
             `h-viewport` flex column and its row is `min-h-0`, so at `lg` the
             panel is an ordinary flex item whose height is the row's: it
             never grows with the page, because the page no longer scrolls the
             window — `<main data-app-scroll>` does. No `sticky` is needed.
             Below `lg` the panel is `hidden`.

             Keep `overflow-*` off the row that wraps this panel and `<main>`:
             `<main>` owns the scroll, and the panel's own `<nav>` scrolls
             inside it.

          3. `lg:w-60` (240px). Scoped to `lg:` because below it the panel is
             not displayed.

             The 240 is NOT free to move on its own: it is
             `appNavRailWidth(…)` in `src/lib/ui/appNavRail.ts`, which seeds
             the first render of every `/chat` and `/tasks` breakpoint
             decision (`useViewportRange`) and places the toast.
             `Sidebar.test.tsx` fails if the token and the function disagree.

             Story 24.6 — THE ICON RAIL (1024–1279px, unpinned): `lg:w-16`
             (64px) instead of `lg:w-60`, every other class unchanged, so the
             rail is still in flow and pinning it to 240 PUSHES the content
             (no overlay, and no width animation — the swap is instant).
             64 − the nav's `px-2` (16) = 48px icon-only rows, above the 44px
             floor. That holds only because the nav HIDES ITS SCROLLBAR while
             railed (`[scrollbar-width:none]`): a classic 15px scrollbar would
             leave 33px rows. The nav still scrolls by wheel, touch and focus.
             At ≥1280 (`xl`) the sidebar is 240 whatever the pin, so the
             toggle only renders in the rail band.

             🔴 THE WIDTH BUDGET. Labels are `type-label` (Inter 14px/500),
             MEASURED in headless Chromium against the real webfont — never
             estimated (per-character estimates run ~11% low). The nav's
             `px-2` (16) and a classic scrollbar (~15, the nav really scrolls
             at 600px tall with Engineering open) come off first:

               top row    = 240 − 16 − 15 − px-3 (24) − icon (20) − mr-3 (12)
                          = 153px   vs `Documentation`
               child row  = 240 − 16 − 15 − ml-4 (16) − px-2 (16) − icon (20)
                          − mr-2 (8) = 149px   vs `Prompt Management` (139)
               toggle     = top row − chevron (16) = 137px   vs `Engineering`

             The child rows are the binding ones. `Sidebar.test.tsx` holds the
             measured table and a 6px safety margin.

             Belt and braces: every label is `truncate`d with a `title`, so a
             future label that outgrows its box ellipsises instead of silently
             wrapping. */}
      <aside
        id={SIDEBAR_ID}
        className={`hidden lg:flex flex-col z-40 bg-canvas border-r border-line ${railed ? 'lg:w-16' : 'lg:w-60'} lg:h-full`}
      >
        {/* The ONE scroll area (A3). `flex-1` takes the panel's remaining
            height and `min-h-0` lets it actually shrink below its content
            (a flex child's `min-height: auto` floor is otherwise its content,
            and the column would spill out of a viewport-bounded panel).
            `overscroll-contain` stops a scroll that reaches the end chaining
            to the page behind the panel. */}
        {/* One provider for the rail's tooltips, so moving between icons
            skips the 500ms delay after the first one opens. */}
        <TooltipProvider>
          <nav
            aria-label="Main"
            className={`flex-1 min-h-0 overflow-y-auto overscroll-contain px-2 pt-5 pb-2${
              railed ? ' [scrollbar-width:none] [&::-webkit-scrollbar]:hidden' : ''
            }`}
          >
            <ul role="list" aria-label="Work" className="space-y-1">
              {WORK_NAV.map(renderRow)}
            </ul>

            {/* Groups are separated by space and a hairline, with no visible
                heading; each carries an accessible name only. Documentation and
                Settings are role-open, so this group is never empty. */}
            <ul role="list" aria-label="Help & admin" className="mt-4 pt-4 border-t border-line space-y-1">
              {renderRow(DOCUMENTATION_ROW)}
              {renderRow(SETTINGS_ROW)}
              {showEngineering && (
                <li>
                  {/* Story PPA-2.1 + T-20260916-2: a non-superuser gets no
                      Engineering heading in the DOM at all — not a CSS-hidden
                      one, and not a live chevron over an empty panel. */}
                  <SidebarSubmenu
                    title={ENGINEERING_TITLE}
                    icon={NAV_ICONS.engineering.outline}
                    activeIcon={NAV_ICONS.engineering.solid}
                    items={superAdminNavigation}
                    isOpen={engineeringOpen}
                    onToggle={() => setEngineeringOpen((open) => !open)}
                    isOwner={viewer.isOwner}
                    isSuperuser={viewer.isSuperuser}
                    rail={railed}
                  />
                </li>
              )}
            </ul>
          </nav>

          {/* Story 24.6 — the pin toggle, at the foot of the column and outside
              the scroll area. Only in the rail band: at ≥1280 the sidebar is
              always full, and below 1024 it is not rendered. */}
          {band === 'rail' && (
            <div className={`flex-shrink-0 flex border-t border-line p-2 ${pinned ? 'justify-end' : 'justify-center'}`}>
              <WithTooltip asLabel side="right" sideOffset={APP_NAV_RAIL_TOOLTIP_OFFSET} content={pinLabel}>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={togglePinned}
                  aria-expanded={pinned}
                  aria-controls={SIDEBAR_ID}
                  data-testid="sidebar-pin-toggle"
                >
                  <PinIcon aria-hidden="true" />
                </Button>
              </WithTooltip>
            </div>
          )}
        </TooltipProvider>
      </aside>
    </>
  );
}
